import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { formatMoney, formatPercent } from '@revenuemarket/utils';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import { FinanceCard } from '~apis/dashboard';
import { useCompany } from '~hooks/queries/companies/query';
import useTermOfferQuery from '~hooks/queries/useTermOfferQuery';
import { useTradeAllowed } from '~hooks/tax-invoice/useTradeAllowed';
import { useCompanyId } from '~hooks/useCompanyId';

import TradeRevenueButton from './buttons/TradeRevenueButton';
import { Pannel } from './Pannel';
interface Props {
  expectedPayout: number;
  tradeApplyDate: string;
  discountRate: number;
  repaymentPeriod: number;
  payoutDate: string;
  repaymentSchedules: {
    repaymentRound: number;
    repaymentDate: string;
    repaymentAmount: number;
    principalAmount: number;
    discountAmount: number;
  }[];
  // 지급/회수 section 노출여부
  showPayoutSchedule?: boolean;
  mainActionTitle: string;
  onClickMainAction: () => void;
  ellipsis?: boolean;
  item: FinanceCard;
}

const renderButtonGroups = ({
  mainActionTitle,
  onClickMainAction,
  isDisabled,
  payoutDate,
  hideReservationButton,
}: {
  mainActionTitle: string;
  onClickMainAction: () => void;
  isDisabled: boolean;
  payoutDate: string;
  hideReservationButton?: boolean;
}) => {
  return (
    <Flex direction="column" gap={4}>
      <TradeRevenueButton
        buttonType={hideReservationButton ? 'weak' : 'default'}
        border={!hideReservationButton}
        title={mainActionTitle}
        onClick={onClickMainAction}
      />
      {isDisabled && (
        <Typography.Text
          style={{
            ...typographyStyle['13_400'],
            color: Colors.gray700,
          }}
        >
          {dayjs(payoutDate).format('YYYY년 M월 D일')} 일정은
          <br />
          선착순 신청이 마감됐어요.
        </Typography.Text>
      )}
    </Flex>
  );
};

const TermInfo = ({
  tradeApplyDate,
  payoutDate,
  mainActionTitle,
  onClickMainAction: onClickMainAction,
}: Props) => {
  const { companyId } = useCompanyId();
  const company = useCompany({ companyId });
  const { data: termOfferData } = useTermOfferQuery(companyId);
  const termOffer = termOfferData?.Data?.data;

  const isDisabled = !useTradeAllowed(companyId);
  const today = dayjs();
  const diffDate = Math.ceil(dayjs(tradeApplyDate).diff(today, 'day', true));
  const dDay = dayjs(tradeApplyDate).isSame(today, 'day')
    ? 'D-day'
    : dayjs(tradeApplyDate).isAfter(today)
      ? `D-${diffDate}`
      : '';
  return (
    <Flex direction="column" gap={20}>
      <Pannel>
        <Flex direction="column" gap={4}>
          <Typography.Text
            style={{ ...typographyStyle['18_600'], color: Colors.gray800, marginBottom: 20 }}
          >
            {`${company.data?.Data?.data?.name}의 선택 가능한 기간별 조건`}
          </Typography.Text>
          <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
            지급일
          </Typography.Text>
          <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
            {dayjs(payoutDate).format('YYYY년 M월 D일')}
          </Typography.Text>
        </Flex>
        <Flex direction="column" gap={4}>
          <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
            신청 마감일
          </Typography.Text>
          <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
            {`${dayjs(tradeApplyDate).format('YYYY년 M월 D일')} (${dDay})`}
          </Typography.Text>
        </Flex>
        {termOffer && (
          <Flex direction="column" gap={8}>
            <Typography.Text
              style={{
                ...typographyStyle['13_400'],
                color: Colors.gray800,
              }}
            >
              아래 회수 기간 중 하나를 선택할 수 있어요.
              <br />
              회수는 지급일 기준으로 1개월 뒤부터 매달 같은 날짜에 선택한 기간동안 자동 진행돼요.
            </Typography.Text>
            <Flex direction="column" gap={8}>
              <Flex direction="row" gap={4}>
                <Flex style={{ width: 100 }}>
                  <Typography.Text style={{ ...typographyStyle['13_600'], color: Colors.gray900 }}>
                    회수 기간
                  </Typography.Text>
                </Flex>
                <Flex style={{ width: 100, justifyContent: 'flex-end' }}>
                  <Typography.Text style={{ ...typographyStyle['13_600'], color: Colors.gray900 }}>
                    할인율
                  </Typography.Text>
                </Flex>
                <Flex style={{ flex: 1, justifyContent: 'flex-end' }}>
                  <Typography.Text style={{ ...typographyStyle['13_600'], color: Colors.gray900 }}>
                    최대 한도
                  </Typography.Text>
                </Flex>
              </Flex>
              {termOffer.termOptions?.map((termOption) => {
                return (
                  <Flex key={termOption.tradingPeriod} direction="row" gap={4}>
                    <Flex style={{ width: 100 }}>
                      <Typography.Text
                        style={{ ...typographyStyle['14_400'], color: Colors.gray700 }}
                      >
                        {termOption.tradingPeriod}개월
                      </Typography.Text>
                    </Flex>
                    <Flex style={{ width: 100, justifyContent: 'flex-end' }}>
                      <Typography.Text
                        style={{ ...typographyStyle['14_400'], color: Colors.gray700 }}
                      >
                        {formatPercent(termOption.tradingDiscountRate, 2)}
                      </Typography.Text>
                    </Flex>
                    <Flex style={{ flex: 1, justifyContent: 'flex-end' }}>
                      <Typography.Text
                        style={{ ...typographyStyle['14_400'], color: Colors.gray700 }}
                      >
                        {formatMoney(termOption.payoutLimit, undefined, undefined, '원')}
                      </Typography.Text>
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        )}
      </Pannel>
      {renderButtonGroups({
        mainActionTitle,
        onClickMainAction,
        isDisabled,
        payoutDate,
        hideReservationButton: true,
      })}
    </Flex>
  );
};

export default TermInfo;
